/**
 * A divergência entre trilhas — o bloco que justifica a revisão existir.
 *
 * **O caso é literal.** A ONB-3114 negou controle de estoque no Força de Vendas e, trinta minutos
 * depois, descreveu controle de estoque por integração no formulário de Estoque. Quem leu a issue
 * não tinha como perceber, e a divergência só apareceu na configuração — que é quando custa caro.
 * O motor detecta isso desde a F5; a spec §2.3 pede que ele **apresente** a detecção a quem
 * revisa.
 *
 * Três decisões deste componente:
 *
 * · **O portal não escolhe qual valor vale.** Mostra os dois, com a trilha de cada um, e pronto.
 *   Contradição não tem tabela pelo mesmo motivo que a esteira não tem coluna de etapa: resolver
 *   é leitura humana, e guardar a resolução seria um segundo lugar para contar a mesma história.
 *
 * · **O silêncio também é informação.** Quando não há divergência, dizer isso é diferente de não
 *   mostrar nada: sem a frase, quem revisa não sabe se o portal conferiu e não achou ou se a
 *   conferência não existe. A segunda já foi verdade.
 *
 * · **Um componente só para as duas telas.** A tela da implantação e a revisão mostram a mesma
 *   divergência; duas renderizações voltariam a poder divergir entre si — que é, com ironia
 *   exata, o defeito que este bloco existe para denunciar. O que muda entre elas é só o gesto: na
 *   revisão cada divergência oferece devolver a trilha ao cliente, porque é ali que o consultor
 *   age.
 */

import { Aviso, Botao } from "../ui";
import { ROTULO_DA_CONTRADICAO, type ContradicaoEntreTrilhas } from "../../nucleo/kickoff";

export interface PropsDasContradicoes {
  contradicoes: readonly ContradicaoEntreTrilhas[];
  /**
   * Devolver a trilha a partir da própria divergência — o que liga a detecção ao conserto.
   *
   * Ausente na tela da implantação: lá o bloco é leitura, e o ato mora na revisão. Presente só
   * para quem tem `kickoff:revisar`, porque oferecer um botão que o servidor nega ensina a pessoa
   * a ignorar recusa.
   */
  aoApontar?: (trilha: string, perguntaId: string) => void;
  className?: string;
}

export function Contradicoes({
  contradicoes,
  aoApontar,
  className = "mb-4",
}: PropsDasContradicoes) {
  if (contradicoes.length === 0) {
    return (
      <Aviso tom="sucesso" className={className} data-testid="sem-contradicoes">
        Nenhuma divergência entre as trilhas respondidas até agora.
      </Aviso>
    );
  }

  return (
    /* Aviso, não impedimento (spec de 28/09, 6.9): a divergência pode ser legítima — o B2B pode
       funcionar de um jeito e o Força de Vendas de outro. Por isso o tom é de atenção, não de
       erro, e nada no portal trava por ela: aceitar e gerar o resumo continuam disponíveis. */
    <Aviso tom="atencao" className={className} data-testid="contradicoes">
      <b>
        Aviso:{" "}
        {contradicoes.length === 1
          ? "1 pergunta foi respondida de formas diferentes em trilhas diferentes."
          : `${contradicoes.length} perguntas foram respondidas de formas diferentes em trilhas diferentes.`}
      </b>{" "}
      Pode ser legítimo — um módulo pode funcionar de um jeito e outro de outro. O portal não
      escolhe qual vale — confira antes de fechar.
      <ul className="lista-de-impedimentos">
        {contradicoes.map((item) => (
          <li
            key={`${item.pergunta_a}|${item.pergunta_b}`}
            data-testid={`contradicao-${item.pergunta_a}`}
          >
            <b>{item.enunciado_a}</b> — {ROTULO_DA_CONTRADICAO[item.estado]}
            {/* Nome da trilha e resposta pelo rótulo, as duas vindas do servidor. O envelope cru
                ("opcoes: integrar_do_erp") era jargão de campo no meio da frase (spec 6.8). */}
            <div className="celula-secundaria">
              {item.nome_da_trilha_a}: {item.resposta_a}
            </div>
            <div className="celula-secundaria">
              {item.nome_da_trilha_b}: {item.resposta_b}
            </div>
            {aoApontar ? (
              <div className="acoes-do-console mt-2">
                {/* Devolve a trilha de onde saiu o primeiro valor, já apontando a pergunta: com
                    `pergunta_id` o cliente cai na pergunta; sem ele, cai na trilha e procura. */}
                <Botao
                  variante="fantasma"
                  tamanho="pequeno"
                  data-testid="apontar-pendencia"
                  onClick={() => aoApontar(item.trilha_a, item.pergunta_a)}
                >
                  Devolver ao cliente
                </Botao>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </Aviso>
  );
}
